import React, { useState, useEffect } from 'react';
import { 
    Paper, Typography, Box, TextField, CircularProgress,
    Alert, Grid, IconButton
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, 
    Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import DataTable from './DataTable';

// Helper for graph time formatting: show only HH:mm (no seconds)
const formatHourMinute = (dateTime) => {
    if (!dateTime) return '';
    // Handles "YYYY-MM-DD HH:mm:ss"
    if (dateTime.includes(' ')) {
        const time = dateTime.split(' ')[1] || '';
        const [hh, mm] = time.split(':');
        return `${hh}:${mm}`;
    }
    // Handles ISO format
    if (dateTime.includes('T')) {
        const t = dateTime.split('T')[1];
        if (t) {
            const [hh, mm] = t.split(':');
            return `${hh}:${mm}`;
        }
    }
    return dateTime;
};

// Helper for tooltip: show full date and time in "YYYY-MM-DD HH:mm:ss" format
const formatFullDateTime = (dateTime) => {
    if (!dateTime) return '';
    if (dateTime.includes(' ')) {
        return dateTime;
    }
    if (dateTime.includes('T')) {
        return dateTime.replace('T', ' ').split('.')[0];
    }
    return dateTime;
};

const FEED_UNIT = 'm³';
const COND_UNIT = 'm³';
const POWER_FLOW_UNIT = 'kW';
const POWER_CONS_UNIT = 'kW';

const FEED_COLOR = '#82ca9d';
const COND_COLOR = '#ffc658';
const POWER_FLOW_COLOR = '#8884d8';
const POWER_CONS_COLOR = '#ff7300';

const FixedRangeView = ({ tableName }) => {
    const [selectedDate, setSelectedDate] = useState(
        new Date().toISOString().split('T')[0]
    );
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Always use dash-case for tableName in API call
                const apiTableName = tableName.toLowerCase().replace(/ /g, '-');
                const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
                const response = await fetch(
                    `${apiBaseUrl}/${apiTableName}/fixedrange?date=${selectedDate}`
                );
                if (!response.ok) throw new Error('Failed to fetch data');
                const result = await response.json();
                setData(result);
                setError(null);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [tableName, selectedDate]);

    // Show correct keys and units for mohali, otherwise use generic logic
    const getDataKeys = () => {
        if (!data.length) return [];
        if (tableName.toLowerCase() === 'mohali') {
            return [
                {
                    key: 'EM_POWER_PRE_1HR',
                    name: 'Power (1 Hour) (kW)',
                    color: '#8884d8',
                    yAxisId: 'power1hr'
                },
                {
                    key: 'EM_POWER_PRE_24HR',
                    name: 'Power (24 Hours) (kW)',
                    color: '#82ca9d',
                    yAxisId: 'power24hr'
                }
            ];
        }
        if (tableName.toLowerCase() === 'ks-textile') {
            return [
                {
                    key: 'FEED_FLOW_PRE_1H_TOT_KS',
                    name: 'Feed Flow (m³)',
                    color: FEED_COLOR,
                    yAxisId: 'feed'
                },
                {
                    key: 'COND_FLOW_PRE_1H_TOT_KS',
                    name: 'Cond Flow (m³)',
                    color: COND_COLOR,
                    yAxisId: 'cond'
                },
                {
                    key: 'POWER_PRE_1H_TOT_KS',
                    name: 'Power Consumption (kW)',
                    color: POWER_FLOW_COLOR,
                    yAxisId: 'powerflow'
                }
            ];
        }
        if (tableName.toLowerCase() === 'clear-choice') {
            return [
                {
                    key: 'FEED_FLOW_TOT_PRE_1H_CCHOICE',
                    name: 'Feed Flow (m³)',
                    color: FEED_COLOR,
                    yAxisId: 'feed'
                },
                {
                    key: 'COND_FLOW_TOT_PRE_1H_CCHOICE',
                    name: 'Cond Flow (m³)',
                    color: COND_COLOR,
                    yAxisId: 'cond'
                },
                {
                    key: 'EM_POWER_TOT_PRE_1H_CCHOICE',
                    name: 'Power Consumption (kW)',
                    color: POWER_FLOW_COLOR,
                    yAxisId: 'powerflow'
                }
            ];
        }
        if (tableName.toLowerCase() === 'baddi') {
            return [
                {
                    key: 'Baddi_Power_PRE_1h_TOT',
                    name: 'Baddi Power (1 Hour) (kW)',
                    color: '#8884d8',
                    yAxisId: 'power1hr'
                },
                {
                    key: 'EM2_POWER_PRE_1H_TOT_HMI',
                    name: 'EM2 Power (1 Hour) (kW)',
                    color: '#82ca9d',
                    yAxisId: 'power2hr'
                }
            ];
        }
        if (tableName.toLowerCase() === 'parle') {
            return [
                {
                    key: 'FEED_FLOW_PRE_1H_TOT_PARLE',
                    name: 'Feed Flow (m³)',
                    color: FEED_COLOR,
                    yAxisId: 'feed'
                },
                {
                    key: 'COND_FLOW_TOT_PRE_1HR_PARLE',
                    name: 'Cond Flow (m³)',
                    color: COND_COLOR,
                    yAxisId: 'cond'
                },
                {
                    key: 'POWER_PRE_1H_TOT_PARLE',
                    name: 'Power Consumption (kW)',
                    color: POWER_FLOW_COLOR,
                    yAxisId: 'powerflow'
                }
            ];
        }
        const keys = Object.keys(data[0]);
        const feedKey = keys.find(k => k.toUpperCase().includes('FEED'));
        const condKey = keys.find(k => k.toUpperCase().includes('COND'));
        const powerFlowKey = keys.find(k =>
            k.toUpperCase().includes('POWER') &&
            !k.toUpperCase().includes('CONSUM') &&
            !k.toUpperCase().includes('EM_POWER_PRE_1HR') &&
            !k.toUpperCase().includes('EM_POWER_PRE_24HR')
        );
        const powerConsKey = keys.find(k =>
            k.toUpperCase().includes('CONSUM') ||
            k.toUpperCase().includes('EM_POWER_PRE_1HR') ||
            k.toUpperCase().includes('EM_POWER_PRE_24HR')
        );

        return [
            feedKey && {
                key: feedKey,
                name: `Feed Flow (${FEED_UNIT})`,
                color: FEED_COLOR,
                yAxisId: 'feed'
            },
            condKey && {
                key: condKey,
                name: `Cond Flow (${COND_UNIT})`,
                color: COND_COLOR,
                yAxisId: 'cond'
            },
            powerFlowKey && {
                key: powerFlowKey,
                name: `Power Consumption (${POWER_FLOW_UNIT})`,
                color: POWER_FLOW_COLOR,
                yAxisId: 'powerflow'
            },
            powerConsKey && {
                key: powerConsKey,
                name: `Power Consumption (${POWER_CONS_UNIT})`,
                color: POWER_CONS_COLOR,
                yAxisId: 'powercons'
            }
        ].filter(Boolean);
    };

    // Get all yAxisIds used in the current data keys
    const getActiveYAxisIds = () => {
        return getDataKeys().map(k => k.yAxisId);
    };

    const handleDateChange = (event) => {
        setSelectedDate(event.target.value);
    };

    const handlePrevDay = () => {
        const prev = new Date(selectedDate);
        prev.setDate(prev.getDate() - 1);
        setSelectedDate(prev.toISOString().split('T')[0]);
    };

    const handleNextDay = () => {
        const next = new Date(selectedDate);
        next.setDate(next.getDate() + 1);
        setSelectedDate(next.toISOString().split('T')[0]);
    };

    const getDisplayDateRange = (selectedDate) => {
        const date = new Date(selectedDate);
        const prevDate = new Date(date);
        prevDate.setDate(prevDate.getDate() - 1);
        
        const formatDate = (d) => {
            return d.toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
            });
        };

        return {
            start: `${formatDate(prevDate)} 7:00 AM`,
            end: `${formatDate(date)} 6:00 AM`
        };
    };

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box sx={{ mt: 3 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        {/* Flex row: heading left, date right with arrows */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Fixed Range View
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton aria-label="Previous day" onClick={handlePrevDay} size="small">
                                    <ArrowBackIosNewIcon fontSize="small" />
                                </IconButton>
                                <TextField
                                    type="date"
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                    sx={{ minWidth: 180 }}
                                    size="small"
                                />
                                <IconButton aria-label="Next day" onClick={handleNextDay} size="small">
                                    <ArrowForwardIosIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Showing data from {getDisplayDateRange(selectedDate).start} to {getDisplayDateRange(selectedDate).end}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        {loading ? (
                            <Box display="flex" justifyContent="center">
                                <CircularProgress />
                            </Box>
                        ) : data.length > 0 ? (
                            <Box sx={{ height: 400 }}>
                                <ResponsiveContainer>
                                    <LineChart data={data}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis 
                                            dataKey="DateTime" 
                                            tickFormatter={formatHourMinute}
                                            angle={-45}
                                            textAnchor="end"
                                            height={80}
                                        />
                                        {/* Mohali Y axes */}
                                        {tableName.toLowerCase() === 'mohali' ? (
                                            <>
                                                <YAxis
                                                    yAxisId="power1hr"
                                                    label={{
                                                        value: 'Power (1 Hour) (kW)',
                                                        angle: -90,
                                                        position: "insideLeft",
                                                        style: { textAnchor: 'middle', fontWeight: 600, fill: '#8884d8' }
                                                    }}
                                                    stroke="#8884d8"
                                                    width={70}
                                                />
                                                <YAxis
                                                    yAxisId="power24hr"
                                                    orientation="right"
                                                    label={{
                                                        value: 'Power (24 Hours) (kW)',
                                                        angle: 90,
                                                        position: "insideRight",
                                                        style: { textAnchor: 'middle', fontWeight: 600, fill: '#82ca9d' }
                                                    }}
                                                    stroke="#82ca9d"
                                                    width={70}
                                                    tick={{ dx: 10 }}
                                                />
                                            </>
                                        ) : tableName.toLowerCase() === 'baddi' ? (
                                            <>
                                                {/* Baddi Power Y Axis (left) */}
                                                <YAxis
                                                    yAxisId="power1hr"
                                                    label={{
                                                        value: 'Baddi Power (1 Hour) (kW)',
                                                        angle: -90,
                                                        position: "insideLeft",
                                                        style: { textAnchor: 'middle', fontWeight: 600, fill: '#8884d8' }
                                                    }}
                                                    stroke="#8884d8"
                                                    width={70}
                                                />
                                                {/* EM2 Power Y Axis (right) */}
                                                <YAxis
                                                    yAxisId="power2hr"
                                                    orientation="right"
                                                    label={{
                                                        value: 'EM2 Power (1 Hour) (kW)',
                                                        angle: 90,
                                                        position: "insideRight",
                                                        style: { textAnchor: 'middle', fontWeight: 600, fill: '#82ca9d' }
                                                    }}
                                                    stroke="#82ca9d"
                                                    width={70}
                                                    tick={{ dx: 10 }}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                {/* Dynamically render YAxis only for active yAxisIds */}
                                                {getActiveYAxisIds().includes('feed') && (
                                                    <YAxis
                                                        yAxisId="feed"
                                                        label={{
                                                            value: 'Feed Flow (m³)',
                                                            angle: -90,
                                                            position: "insideLeft",
                                                            style: { textAnchor: 'middle', fontWeight: 600 }
                                                        }}
                                                        stroke={FEED_COLOR}
                                                    />
                                                )}
                                                {getActiveYAxisIds().includes('cond') && (
                                                    <YAxis
                                                        yAxisId="cond"
                                                        orientation="right"
                                                        label={{
                                                            value: 'Cond Flow (m³)',
                                                            angle: 90,
                                                            position: "insideRight",
                                                            style: { textAnchor: 'middle', fontWeight: 600 }
                                                        }}
                                                        stroke={COND_COLOR}
                                                        width={60}
                                                        tick={{ dx: 10 }}
                                                    />
                                                )}
                                                {getActiveYAxisIds().includes('powerflow') && (
                                                    <YAxis
                                                        yAxisId="powerflow"
                                                        orientation="right"
                                                        label={{
                                                            value: 'Power Consumption (kW)',
                                                            angle: 90,
                                                            position: "outsideRight",
                                                            style: {
                                                                textAnchor: 'middle',
                                                                fontWeight: 700,
                                                                fill: POWER_FLOW_COLOR,
                                                                fontSize: 16,
                                                                letterSpacing: 1,
                                                                fontFamily: 'Roboto, Arial, sans-serif'
                                                            }
                                                        }}
                                                        stroke={POWER_FLOW_COLOR}
                                                        width={90}
                                                        tick={{ dx: 60, fill: POWER_FLOW_COLOR, fontWeight: 600, fontSize: 14 }}
                                                        axisLine={{ stroke: POWER_FLOW_COLOR, strokeWidth: 2 }}
                                                    />
                                                )}
                                                {getActiveYAxisIds().includes('powercons') && (
                                                    <YAxis
                                                        yAxisId="powercons"
                                                        orientation="right"
                                                        label={{
                                                            value: 'Power Consumption (kW)',
                                                            angle: 90,
                                                            position: "outsideRight",
                                                            offset: 60,
                                                            style: { textAnchor: 'middle', fontWeight: 600 }
                                                        }}
                                                        stroke={POWER_CONS_COLOR}
                                                        width={60}
                                                        tick={{ dx: 90 }}
                                                    />
                                                )}
                                            </>
                                        )}
                                        <Tooltip 
                                            labelFormatter={formatFullDateTime}
                                            formatter={(value, name, props) => {
                                                if (tableName.toLowerCase() === 'mohali') {
                                                    if (props.dataKey === 'EM_POWER_PRE_1HR') {
                                                        return [`${Number(value).toFixed(2)} kW`, 'Power (1 Hour)'];
                                                    }
                                                    if (props.dataKey === 'EM_POWER_PRE_24HR') {
                                                        return [`${Number(value).toFixed(2)} kW`, 'Power (24 Hours)'];
                                                    }
                                                } else {
                                                    const keys = Object.keys(data[0]);
                                                    const feedKey = keys.find(k => k.toUpperCase().includes('FEED'));
                                                    const condKey = keys.find(k => k.toUpperCase().includes('COND'));
                                                    const powerFlowKey = keys.find(k =>
                                                        k.toUpperCase().includes('POWER') &&
                                                        !k.toUpperCase().includes('CONSUM') &&
                                                        !k.toUpperCase().includes('EM_POWER_PRE_1HR') &&
                                                        !k.toUpperCase().includes('EM_POWER_PRE_24HR')
                                                    );
                                                    const powerConsKey = keys.find(k =>
                                                        k.toUpperCase().includes('CONSUM') ||
                                                        k.toUpperCase().includes('EM_POWER_PRE_1HR') ||
                                                        k.toUpperCase().includes('EM_POWER_PRE_24HR')
                                                    );
                                                    const key = props.dataKey;
                                                    if (key && key === feedKey) {
                                                        return [`${Number(value).toFixed(2)} ${FEED_UNIT}`, 'Feed Flow'];
                                                    }
                                                    if (key && key === condKey) {
                                                        return [`${Number(value).toFixed(2)} ${COND_UNIT}`, 'Cond Flow'];
                                                    }
                                                    if (key && key === powerFlowKey) {
                                                        return [`${Number(value).toFixed(2)} ${POWER_FLOW_UNIT}`, 'Power Consumption'];
                                                    }
                                                    if (key && key === powerConsKey) {
                                                        return [`${Number(value).toFixed(2)} ${POWER_CONS_UNIT}`, 'Power Consumption'];
                                                    }
                                                }
                                                return [value, name];
                                            }}
                                        />
                                        <Legend />
                                        {getDataKeys().map(({ key, name, color, yAxisId }) => (
                                            <Line
                                                key={key}
                                                type="monotone"
                                                dataKey={key}
                                                name={name}
                                                stroke={color}
                                                dot={false}
                                                strokeWidth={2}
                                                yAxisId={yAxisId}
                                            />
                                        ))}
                                    </LineChart>
                                </ResponsiveContainer>
                            </Box>
                        ) : (
                            <Typography color="textSecondary">
                                Please select a date to view data
                            </Typography>
                        )}
                    </Grid>
                </Grid>
                <Typography variant="caption" display="block" gutterBottom>
                    Showing data from 7:00 AM - 6:00 AM
                </Typography>
                <Typography variant="caption" display="block">
                    Data range: {data[0]?.DateTime} to {data[data.length - 1]?.DateTime}
                </Typography>
            </Paper>
            
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Data Table</Typography>
                <DataTable 
                    data={data} 
                    tableName={tableName.toUpperCase().replace('-', ' ')}
                    loading={loading}
                />
            </Paper>
        </Box>
    );
};

export default FixedRangeView;
